import { useEffect, useState } from "react";
import { PortalHelpButton } from "portal-help";
import { useLocation, useNavigate } from "react-router-dom";
import { useLobRegion } from "../../hooks/useLobRegion";
import { useLobName } from "../../hooks/useLobName";
import { Sidebarlist } from "./SidebarList";
import logo from "../../assets/images/logo.png";
import { Home, FileText, LayoutDashboard, Upload, Truck, Store, Shield, LogOut, Menu, Bell, Castle, BadgeDollarSign, BarChart3, Scale, Receipt } from 'lucide-react';
import {
  canManageRbac,
  canManageUsers,
  canUploadVpa,
  canViewDashboard,
  canViewDistributors,
  canViewMis,
  canViewNotifications,
  canViewOutlets,
  canViewPrincipals,
  canViewPrograms,
  canViewPurposeCodes,
  canViewRecon,
  canViewReports,
  canViewVouchers,
} from "../../utils/permissions";

interface ComponentProps {
  title: string;
  icon: JSX.Element;
  screenName: string;
  state: boolean;
}

// Outlets, VPA Upload, Recon and Purpose Code are UPI/eRupi-centric, so they only exist for India-style LoBs.
const getSidebarItems = (indiaFields: boolean): ComponentProps[] => {
  const items: ComponentProps[] = [];

  if (canViewDashboard()) {
    items.push({ title: 'Dashboard', icon: <Home className="w-5 h-5" strokeWidth={2} />, screenName: '/', state: true });
  }
  if (canViewPrincipals()) {
    items.push({ title: 'Principals', icon: <Castle className="w-5 h-5" strokeWidth={2} />, screenName: '/principals', state: true });
  }
  if (canViewPrograms()) {
    items.push({ title: 'Programs', icon: <LayoutDashboard className="w-5 h-5" strokeWidth={2} />, screenName: '/programs', state: true });
  }
  if (canViewDistributors()) {
    items.push({ title: 'Distributors', icon: <Truck className="w-5 h-5" strokeWidth={2} />, screenName: '/distributors', state: true });
  }
  if (indiaFields && canViewOutlets()) {
    items.push({ title: 'Outlets', icon: <Store className="w-5 h-5" strokeWidth={2} />, screenName: '/outlets', state: true });
  }
  if (indiaFields && canUploadVpa()) {
    items.push({ title: 'VPA Upload', icon: <Upload className="w-5 h-5" strokeWidth={2} />, screenName: '/vpa-upload', state: true });
  }
  if (canViewVouchers()) {
    items.push({ title: 'Vouchers', icon: <BadgeDollarSign className="w-5 h-5" strokeWidth={2} />, screenName: '/erupi-voucher', state: true });
  }
  if (canViewReports()) {
    items.push({ title: 'Distributor Report', icon: <FileText className="w-5 h-5" strokeWidth={2} />, screenName: '/reports/distributor', state: true });
  }
  if (canViewMis()) {
    items.push({ title: 'MIS', icon: <BarChart3 className="w-5 h-5" strokeWidth={2} />, screenName: '/mis', state: true });
  }
  if (indiaFields && canViewRecon()) {
    items.push({ title: 'Recon', icon: <Scale className="w-5 h-5" strokeWidth={2} />, screenName: '/recon', state: true });
  }
  if (indiaFields && canViewPurposeCodes()) {
    items.push({ title: 'Purpose Code', icon: <FileText className="w-5 h-5" strokeWidth={2} />, screenName: '/purpose-codes', state: true });
  }
  if (canViewNotifications()) {
    items.push({ title: 'Notifications', icon: <Bell className="w-5 h-5" strokeWidth={2} />, screenName: '/notifications', state: true });
  }
  if (canManageUsers()) {
    items.push({ title: 'User Management', icon: <Shield className="w-5 h-5" strokeWidth={2} />, screenName: '/user-management', state: true });
  }
  if (canManageRbac()) {
    items.push({ title: 'RBAC management', icon: <Shield className="w-5 h-5" strokeWidth={2} />, screenName: '/rbac-management', state: true });
  }
  
  items.push({ title: 'Billing & Invoicing', icon: <Receipt className="w-5 h-5" strokeWidth={2} />, screenName: '/billing', state: true });
  items.push({ title: 'Logout', icon: <LogOut className="w-5 h-5" strokeWidth={2} />, screenName: '/logout', state: true });
  return items;
};


export const Sidebar = () => {
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
  });
  const location = useLocation();
  const navigate = useNavigate();
  const { indiaFields } = useLobRegion();
  const lobName = useLobName();

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleItemClick = (screenName: string) => {
    if (screenName === '/logout') {
      localStorage.clear();
      navigate('/login', { replace: true });
      return;
    }
    navigate(screenName);
  };

  return (
    <div className="fixed left-0 top-0 h-screen w-72 overflow-hidden flex flex-col">
      {windowSize.width < 600 ? (
        <div className="p-4">
          <Menu className="w-6 h-6 text-gray-600" />
        </div>
      ) : (
        <div className="flex flex-col h-full bg-gray-50 border-r border-cardborder">
          <div className="flex items-center gap-3 ml-8 mr-4 py-8 min-w-0 border-cardborder">
            <img src={logo} alt="Logo" className="h-10 shrink-0" />
            {lobName && (
              <span
                data-testid="lob-name-badge"
                title={lobName}
                className="truncate max-w-[7rem] px-2.5 py-1 rounded-full bg-blue-50 border border-blue-200 text-xs font-semibold text-blue-700"
              >
                {lobName}
              </span>
            )}
          </div>
          <div className="flex-1 pt-8 overflow-y-auto custom-scrollbar px-2">
            <Sidebarlist
              items={getSidebarItems(indiaFields)}
              activeTab={location.pathname}
              title=""
              onItemClick={handleItemClick}
            />
          </div>
          <div className="px-2 pb-3 border-t border-cardborder pt-2">
            <PortalHelpButton />
          </div>
        </div>
      )}
    </div>
  );
};
